import React, { useEffect, useState } from 'react';
import {
    Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, CircularProgress, createFilterOptions,
} from '@mui/material';
import axios from 'axios';
import ApiLink from 'API';
import { useChat } from 'contexts/ChatContext';

// match against fullname + username + branchCode (search by any)
const filterUsers = createFilterOptions({
    stringify: (o) => `${o.fullname || ''} ${o.username || ''} ${o.branchCode || ''}`,
});

export default function NewGroupDialog({ open, onClose, onCreated }) {
    const { authHeaders, myId, loadRooms } = useChat();
    const [users, setUsers] = useState([]);
    const [selected, setSelected] = useState([]);
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        axios
            .get(`${ApiLink}/api/chat/users`, authHeaders())
            .then((r) => setUsers((r.data || []).filter((u) => u._id !== myId)))
            .catch(() => setUsers([]))
            .finally(() => setLoading(false));
        setSelected([]);
        setName('');
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const create = async () => {
        if (selected.length === 0) return;
        setCreating(true);
        try {
            const type = selected.length === 1 && !name.trim() ? 'direct' : 'group';
            const res = await axios.post(
                `${ApiLink}/api/chat/rooms`,
                { name: name.trim(), participantIds: selected.map((u) => u._id), type },
                authHeaders(),
            );
            await loadRooms();
            onCreated && onCreated(res.data);
            onClose();
        } catch (e) {
            // surface minimal error
            console.error('create room error', e?.response?.data?.msg || e.message);
        } finally {
            setCreating(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>ສ້າງກຸ່ມສົນທະນາໃໝ່</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <TextField
                        label="ຊື່ກຸ່ມ (Group name)"
                        size="small"
                        fullWidth
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        helperText="ເລືອກ 1 ຄົນ ແລະ ບໍ່ໃສ່ຊື່ = ແຊດສ່ວນຕົວ"
                        sx={{ mb: 2 }}
                    />
                    <Autocomplete
                        multiple
                        loading={loading}
                        options={users}
                        value={selected}
                        onChange={(e, v) => setSelected(v)}
                        getOptionLabel={(o) => `${o.fullname || o.username}${o.branchCode ? ` (${o.branchCode})` : ''}`}
                        filterOptions={filterUsers}
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip label={option.fullname || option.username} size="small" {...getTagProps({ index })} key={option._id} />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="ເພີ່ມສະມາຊິກ"
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loading ? <CircularProgress size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button color="secondary" onClick={onClose}>ຍົກເລີກ</Button>
                <Button variant="contained" onClick={create} disabled={selected.length === 0 || creating}>
                    {creating ? 'ກຳລັງສ້າງ…' : 'ສ້າງ'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
